import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Lock, MapPin, RefreshCw, Users } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { Textarea } from "@/clientWeb/components/ui/textarea"
import { Badge } from "@/clientWeb/components/ui/badge"
import { Switch } from "@/clientWeb/components/ui/switch"
import { DatePicker } from "@/clientWeb/components/ui/date-picker"
import { TimePicker } from "@/clientWeb/components/ui/time-picker"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/clientWeb/components/ui/select"
import { TimezoneSelect } from "@/clientWeb/components/ui/timezone-select"
import { ContactsDropdown, TimeZonePlannerDialog } from "@/features/meetings"
import type { StepProps } from "./types"

/** Preset durations in minutes — same set used by ScheduleMeeting so the
 *  Meeting / Webinar creation flows feel uniform. */
const DURATION_PRESETS_MIN = [15, 30, 45, 60, 90, 120, 180, 240]

const formatDuration = (minutes: number, hourLabel: string, hrsLabel: string, minsLabel: string): string => {
  if (minutes < 60) return `${minutes} ${minsLabel}`
  const hours = minutes / 60
  return `${hours} ${minutes === 60 ? hourLabel : hrsLabel}`
}

function generatePassword() {
  const lower = "abcdefghijklmnopqrstuvwxyz"
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
  const nums = "0123456789"
  const special = "!@#$%^&*()_+"
  const all = lower + upper + nums + special
  let pwd = lower[Math.floor(Math.random() * lower.length)]
  pwd += upper[Math.floor(Math.random() * upper.length)]
  pwd += nums[Math.floor(Math.random() * nums.length)]
  pwd += special[Math.floor(Math.random() * special.length)]
  for (let i = 4; i < 8; i++) pwd += all[Math.floor(Math.random() * all.length)]
  return pwd
}

export function StepCreateWebinar({ data, onChange, errors = {}, clearError }: StepProps) {
  const t = useTranslation()
  const [plannerOpen, setPlannerOpen] = useState(false)

  const handlePasswordToggle = (checked: boolean) => {
    onChange({
      passwordEnabled: checked,
      password: checked ? generatePassword() : "",
    })
  }

  const getMeetingDateStr = () => {
    if (!data.date) return ""
    return data.date.toISOString().split("T")[0]
  }

  return (
    <div className="space-y-4">
      {/* Title */}
      <div className="space-y-1.5">
        <span className="text-xs sm:text-sm font-semibold text-foreground">
          {t("webinar.webinarTitle")} <span className="text-red-500">*</span>
        </span>
        <Input
          id="wt"
          value={data.title}
          onChange={(e) => { onChange({ title: e.target.value.slice(0, 300) }); clearError?.('title') }}
          placeholder={t("webinar.enterWebinarTitle")}
          className={`h-11 mt-1 ${errors.title ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
        />
        {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
        <p className="text-xs text-muted-foreground text-right">{data.title.length}/300</p>
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <span className="text-xs sm:text-sm font-semibold text-foreground">{t("webinar.description")}</span>
        <Textarea
          id="wd"
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder={t("webinar.whatIsWebinarAbout")}
          rows={3}
          className="resize-none mt-2 bg-card dark:border dark:border-border"
        />
      </div>

      {/* Date / Time / Duration */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="space-y-1.5">
          <span className="text-xs sm:text-sm font-semibold text-foreground">{t("webinar.date")}</span>
          <DatePicker
            date={data.date}
            setDate={(date) => { onChange({ date }); clearError?.('date') }}
            placeholder={t("webinar.selectDate")}
            variant="yearMonth"
            className={`h-10 mt-1 ${errors.date ? 'border-red-500' : ''}`}
          />
          {errors.date && <p className="text-xs text-red-500">{errors.date}</p>}
        </div>
        <div className="space-y-1.5">
          <span className="text-xs sm:text-sm font-semibold text-foreground flex items-center gap-2">
            {t("webinar.startTime")}
          </span>
          {/* Shadcn-style TimePicker — popover with hour/minute columns,
              5-minute granularity. Same component used in ScheduleMeeting. */}
          <TimePicker
            value={data.time}
            step={5}
            onChange={(v) => { onChange({ time: v }); clearError?.('time') }}
            placeholder={t("webinar.selectTime")}
            aria-label={t("webinar.startTime")}
            error={!!errors.time}
            className="h-10 mt-1"
          />
          {errors.time && <p className="text-xs text-red-500">{errors.time}</p>}
        </div>
        <div className="space-y-1.5">
          <span className="text-xs sm:text-sm font-semibold text-foreground">{t("webinar.duration")}</span>
          <Select
            value={String(data.durationHrs * 60 + data.durationMins)}
            onValueChange={(v) => {
              const total = parseInt(v, 10)
              onChange({
                durationHrs: Math.floor(total / 60),
                durationMins: total % 60,
              })
              clearError?.('duration')
            }}
          >
            <SelectTrigger className={`mt-1 ${errors.duration ? 'border-red-500' : ''}`}>
              <SelectValue placeholder={t("webinar.duration")} />
            </SelectTrigger>
            <SelectContent>
              {DURATION_PRESETS_MIN.map((minutes) => (
                <SelectItem key={minutes} value={String(minutes)}>
                  {formatDuration(
                    minutes,
                    t("meetings.schedule.hour"),
                    t("meetings.schedule.hrs"),
                    t("meetings.schedule.mins"),
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.duration && <p className="text-xs text-red-500">{errors.duration}</p>}
        </div>
      </div>

      {/* Timezone */}
      <div className="space-y-2">
        <span className="text-xs sm:text-sm font-semibold text-foreground flex items-center gap-2">
          {t("webinar.timezone")}
        </span>
        <TimezoneSelect
          value={data.timezone}
          onChange={(value) => onChange({ timezone: value })}
          placeholder={t("webinar.selectTimezone")}
          hideIcon
        />
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPlannerOpen(true)}
            className="gap-1.5 text-xs h-8"
          >
            <MapPin className="w-3.5 h-3.5" />
            {t("webinar.addTimeZonePlanner")}
          </Button>
        </div>
        {data.additionalTimezones.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {data.additionalTimezones.map((tz) => (
              <Badge key={tz} variant="secondary" className="px-3 py-1 text-xs bg-primary/10 text-primary border-primary/20">
                {tz}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Host */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="space-y-1.5">
          <span className="text-xs sm:text-sm font-semibold text-foreground">{t("webinar.hostName")}</span>
          <Input id="hname" value={data.hostName}
            onChange={(e) => { onChange({ hostName: e.target.value }); clearError?.('hostName') }}
            placeholder={t("webinar.enterHostName")}
            className={`h-11 mt-2 ${errors.hostName ? 'border-red-500 focus-visible:ring-red-500' : ''}`} />
          {errors.hostName && <p className="text-xs text-red-500">{errors.hostName}</p>}
        </div>
        <div className="space-y-1.5">
          <span className="text-xs sm:text-sm font-semibold text-foreground">{t("webinar.hostEmail")}</span>
          <Input id="hemail" type="email" value={data.hostEmail}
            onChange={(e) => { onChange({ hostEmail: e.target.value }); clearError?.('hostEmail') }}
            placeholder="host@example.com"
            className={`h-11 mt-2 ${errors.hostEmail ? 'border-red-500 focus-visible:ring-red-500' : ''}`} />
          {errors.hostEmail && <p className="text-xs text-red-500">{errors.hostEmail}</p>}
        </div>
      </div>

      {/* Invitees */}
      <div className="space-y-2">
        <span className="text-xs sm:text-sm font-semibold text-foreground flex items-center gap-2">
          {t("webinar.inviteParticipants")}
        </span>
        <ContactsDropdown
          selectedContacts={data.invitees}
          onContactsChange={(contacts) => onChange({ invitees: contacts })}
        />
        <p className="text-xs text-muted-foreground">{t("webinar.maxRegistrants")}: {data.maxRegistrants}</p>
      </div>

      {/* Max registrants */}
      <div className="space-y-1.5">
        <span className="text-xs sm:text-sm font-semibold text-foreground">{t("webinar.maximumRegistrants")}</span>
        <Input id="maxreg" type="number" min={4} value={data.maxRegistrants}
          onChange={(e) => onChange({ maxRegistrants: parseInt(e.target.value) || 4 })}
          className="h-11 max-w-xs mt-2" />
      </div>

      {/* Settings */}
      <div className="space-y-3 pt-2 border-t border-border">
        <p className="text-xs sm:text-sm font-semibold text-foreground">{t("webinar.settings")}</p>

        {/* Password */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs sm:text-sm font-medium text-foreground">{t("webinar.requirePassword")}</p>
                <p className="text-xs text-muted-foreground">{t("webinar.attendeesNeedPassword")}</p>
              </div>
            </div>
            <Switch checked={data.passwordEnabled} onCheckedChange={handlePasswordToggle} />
          </div>
          {data.passwordEnabled && (
            <>
              <div className="flex gap-2 pl-7">
                <Input value={data.password}
                  onChange={(e) => { onChange({ password: e.target.value }); clearError?.('password') }}
                  placeholder={t("webinar.webinarPassword")}
                  className={`flex-1 h-10 ${errors.password ? 'border-red-500 focus-visible:ring-red-500' : ''}`} />
                <Button type="button" variant="outline" size="icon" onClick={() => onChange({ password: generatePassword() })} className="h-10 w-10 shrink-0">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              {errors.password && <p className="text-xs text-red-500 pl-7">{errors.password}</p>}
            </>
          )}
        </div>

        {/* Any time join */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-4 h-4 text-muted-foreground" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-foreground">{t("webinar.allowJoinAnytime")}</p>
              <p className="text-xs text-muted-foreground">{t("webinar.participantsJoinBeforeHost")}</p>
            </div>
          </div>
          <Switch checked={data.anyTimeJoin} onCheckedChange={(v) => onChange({ anyTimeJoin: v })} />
        </div>

        {/* Recording */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs sm:text-sm font-medium text-foreground">{t("webinar.autoRecording")}</p>
            <p className="text-xs text-muted-foreground">{t("webinar.recordWebinarAuto")}</p>
          </div>
          <Switch checked={data.recording} onCheckedChange={(v) => onChange({ recording: v })} />
        </div>
      </div>

      <TimeZonePlannerDialog
        open={plannerOpen}
        onOpenChange={setPlannerOpen}
        meetingDate={getMeetingDateStr()}
        meetingTime={data.time}
        meetingTimezone={data.timezone}
        selectedTimezones={data.additionalTimezones}
        onTimezonesChange={(timezones) => onChange({ additionalTimezones: timezones })}
      />
    </div>
  )
}
